/**
 * Pure helpers for the employment-window denominator used by the
 * Adjust & Lock chip.  Lives in its own module (no React, no axios)
 * so it can be unit-tested without pulling the whole hook graph.
 *
 * Background: the lock-progress chip "X/Y locked" must reflect the
 * employee's *employment window*, not the calendar month. Without
 * this, mid-period joiners can never reach a green "all locked"
 * state, the downstream `lock_status === 'locked'` rollup
 * under-counts the cohort, and the entire payroll run can be
 * silently blocked on a non-issue.
 *
 * Single source of truth principle (see `payroll-period-membership.mdc`):
 * the backend ships `effective_days_in_period` per employee. This
 * module is the deterministic local fallback used when the server
 * payload is missing the field (older API responses) or when only
 * employment dates are available.
 */

/**
 * Compute the lock-progress denominator for an employee in a given period.
 *
 * @param {string}      period          'YYYY-MM' (e.g. '2026-04')
 * @param {string|null} employmentStart ISO date 'YYYY-MM-DD' or null/undefined
 * @param {string|null} employmentEnd   ISO date 'YYYY-MM-DD' or null/undefined
 * @returns {{
 *   effectiveDays: number,
 *   periodStartIso: string,
 *   periodEndIso: string,
 *   windowStartIso: string|null,   // null when employee has no overlap with period
 *   windowEndIso: string|null,
 *   joinedMidPeriod: boolean,      // employmentStart falls strictly inside the period
 *   leftMidPeriod: boolean,        // employmentEnd falls strictly inside the period
 * }}
 */
export const computeEmploymentWindow = (period, employmentStart, employmentEnd) => {
  if (!period || typeof period !== 'string' || !/^\d{4}-\d{2}$/.test(period)) {
    return {
      effectiveDays: 0,
      periodStartIso: '',
      periodEndIso: '',
      windowStartIso: null,
      windowEndIso: null,
      joinedMidPeriod: false,
      leftMidPeriod: false,
    };
  }
  const [year, month] = period.split('-').map(Number);
  const periodStart = new Date(year, month - 1, 1);
  const periodEnd = new Date(year, month, 0); // day 0 of next month = last day of this month
  const pad = (n) => String(n).padStart(2, '0');
  const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const periodStartIso = fmt(periodStart);
  const periodEndIso = fmt(periodEnd);

  // Parse employment dates as local-Y-M-D to avoid timezone drift
  // (`new Date('2026-04-20')` is parsed as UTC and can shift a day
  // depending on the runtime offset — particularly hostile in SGT).
  const parseLocalIso = (s) => {
    if (!s || typeof s !== 'string') return null;
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  };
  const empStart = parseLocalIso(employmentStart);
  const empEnd = parseLocalIso(employmentEnd);

  let windowStart = periodStart;
  let windowEnd = periodEnd;
  if (empStart && empStart > windowStart) windowStart = empStart;
  if (empEnd && empEnd < windowEnd) windowEnd = empEnd;

  if (windowStart > windowEnd) {
    return {
      effectiveDays: 0,
      periodStartIso,
      periodEndIso,
      windowStartIso: null,
      windowEndIso: null,
      joinedMidPeriod: !!(empStart && empStart > periodStart && empStart <= periodEnd),
      leftMidPeriod: !!(empEnd && empEnd >= periodStart && empEnd < periodEnd),
    };
  }

  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const effectiveDays = Math.round((windowEnd - windowStart) / MS_PER_DAY) + 1;

  return {
    effectiveDays,
    periodStartIso,
    periodEndIso,
    windowStartIso: fmt(windowStart),
    windowEndIso: fmt(windowEnd),
    joinedMidPeriod: !!(empStart && empStart > periodStart && empStart <= periodEnd),
    leftMidPeriod: !!(empEnd && empEnd >= periodStart && empEnd < periodEnd),
  };
};
